import { useState, type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import { nativeMemberHandoff } from '@repo/widget-utils/services/fetchers/native-membership';
import Button from 'src/components/common/timeslots-buttons/button/Button';
import Link from 'src/components/common/timeslots-navigation/link/Link';
import { useNativeMemberContext } from './NativeMemberContext';

// Opens the Base-hosted "My page" in a new tab via a one-time SSO handoff token, so the
// member is not asked to log in again. Renders nothing unless a native member is logged in.
// If the new tab is blocked, falls back to a visible link the member can click.
export default function MemberPageButton(): JSX.Element | null {
    const { t } = useLocale();
    const { isNative, isLoggedIn, token } = useNativeMemberContext();
    const [loading, setLoading] = useState(false);
    const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);

    if (!isNative || !isLoggedIn || !token) return null;

    const openMyPage = () => {
        if (loading) return;
        setLoading(true);
        setFallbackUrl(null);
        // Open the tab synchronously inside the click so the popup blocker allows it, then
        // redirect it once the one-time SSO token is minted. We cannot pass 'noopener' to
        // window.open here: it would return null even on success (indistinguishable from a
        // block) and we'd lose the handle needed to redirect, so sever opener manually.
        const tab = window.open('about:blank', '_blank');
        if (tab) tab.opener = null;
        void (async () => {
            try {
                const url = await nativeMemberHandoff(token);
                if (tab && !tab.closed) {
                    // Each click mints and consumes its own token, so reopening always works.
                    tab.location.replace(url);
                } else {
                    // Tab was genuinely blocked: offer a freshly minted, single-use link.
                    setFallbackUrl(url);
                }
            } catch {
                // Leave the button enabled so the member can retry; close the blank tab we opened.
                tab?.close();
            } finally {
                setLoading(false);
            }
        })();
    };

    if (fallbackUrl) {
        return <Link href={fallbackUrl} text={t.my_page} />;
    }

    return (
        <Button
            variant="outlined"
            text={t.my_page}
            onClick={() => openMyPage()}
            disabled={loading}
        />
    );
}
